import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useMatches } from "react-router";

import { useURL } from "~/contexts";
import { useAuth } from "~/hec/context/auth-context";
import { SFL_UPDATE_FAILED } from "~/lib/constants";
import { isStorageBlocked } from "~/lib/utils/safe-storage";

import {
  SaveForLaterContext,
  useSaveForLaterActions,
} from "./hooks/save-for-later-hooks";
import { useFailSoftResourceState } from "./hooks/use-failsoft-resource";
import { useOnFetcherSettled } from "./hooks/use-fetcher-settled";
import { useGuestSaveForLater } from "./hooks/use-guest-save-for-later";
import type { loader } from "./save-for-later";
import {
  RESOURCE_URL,
  SflActionResponseSchema,
} from "./schemas/save-for-later";
import { failedSflProductId } from "./utils/sfl-settle";

const noop = () => {};
// Guest mutators are no-ops for logged-in shoppers (they use the server
// action route); `true` keeps the "write persisted" contract satisfied.
const noopTrue = () => true;

/** Route id React Router derives for `route("/cart", "./routes/cart.tsx")`. */
export const CART_ROUTE_ID = "routes/cart";

/**
 * Hydrates the SFL context from `/resources/save-for-later`. Guests pass
 * their localStorage product IDs via `?id=` so the server can expand them
 * into product cards. Auth state and the guest id list are the re-fetch
 * triggers; same-tab guest mutations come through `useGuestSaveForLater`,
 * cross-tab updates ride the browser's `storage` event inside that hook.
 */
export const SaveForLaterProvider = memo(
  ({ children }: { children: React.ReactNode }) => {
    const url = useURL();
    // Auth from AuthProvider (COM-63 review, NIT-1): the layout loader's data
    // is undefined under the `confirmation-layout` route id, which made this
    // provider run guest mode while every consumer's `useAuth()` said
    // logged-in.
    const { isLoggedIn } = useAuth();
    const guest = useGuestSaveForLater();
    const { addFetcher, removeFetcher, cleanupFetcher } =
      useSaveForLaterActions();

    // COM-63 (P0-1) — only the cart page's SFL section consumes the loaded
    // list; every other consumer (minicart, removed-item-card, move-to-cart)
    // needs just the mutators + auth, so hydrating everywhere cost a wasted
    // SCAPI round-trip per navigation when logged in AND put a failed load's
    // blast radius on every page. Gate the LOAD to /cart; the provider stays
    // layout-mounted so the mutators are available everywhere. Reduced to a
    // boolean first so the memoised context value below doesn't churn on
    // every navigation (`useMatches` returns a new array per nav).
    const isCart = useMatches().some(m => m.id === CART_ROUTE_ID);

    // Auth-only optimistic-hide set for SFL → Cart move. Reconciled against
    // server state via the effect below — entries dropped once the server
    // response confirms they're gone from `productIds` (or readmitted into
    // the list, in which case the next view is authoritative).
    const [optimisticRemoved, setOptimisticRemoved] = useState<Set<string>>(
      () => new Set(),
    );

    // Re-hydrate after ANY SFL mutation settles. useFailSoftResourceState is a
    // raw fetch outside the RR router, so it doesn't get RR7's automatic
    // fetcher revalidation — add / saveLineItem / remove / move-to-cart
    // cleanup must each bump the reload key explicitly (SFCC-11929 review).
    // `addFetcher` covers add + saveLineItem; `removeFetcher` the regular
    // remove; `cleanupFetcher` the SFL → cart move (handled below).
    const [reloadKey, setReloadKey] = useState(0);
    const bumpReload = useCallback(() => setReloadKey(k => k + 1), []);
    useOnFetcherSettled(addFetcher, bumpReload);
    useOnFetcherSettled(removeFetcher, bumpReload);

    // SFL → Cart move cleanup completes on the `sfl-move-cleanup` fetcher
    // (separate key from this provider's loader). On settle: reconcile the
    // server list (bumpReload), AND handle a FAILED cleanup.
    useOnFetcherSettled(cleanupFetcher, data => {
      // COM-63 (P2-7) — a FAILED cleanup (contained network failure →
      // `sfl-error`, or a server-caught SCAPI error → validationError) leaves
      // the item on the server list; the reconcile effect below would then
      // keep it hidden until remount (silent client/server divergence).
      // Un-hide the failed row — just that one when the `sfl-error` envelope
      // names it, everything unconfirmed otherwise (a validationError carries
      // no typed productId) — AND toast: the basket add already succeeded (the
      // ATC flyout is the only feedback otherwise), so the failed SFL removal
      // would be invisible.
      const parsed = SflActionResponseSchema.safeParse(data);
      if (!parsed.success || parsed.data.kind !== "removed") {
        const failedId = failedSflProductId(data);
        setOptimisticRemoved(prev => {
          if (!failedId) return new Set();
          if (!prev.has(failedId)) return prev;
          const next = new Set(prev);
          next.delete(failedId);
          return next;
        });
        toast.error(SFL_UPDATE_FAILED);
      }
      bumpReload();
    });

    // Clear the guest list once the shopper is authenticated (the server owns
    // the list from then on). Re-hydration is driven by `href` / `reloadKey`.
    useEffect(() => {
      if (isLoggedIn) guest.clear();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // Fail-soft hydrate (SFCC-11929): a bare `fetcher.load()` 4xx — e.g. a
    // Cloudflare managed-challenge 403 on this background XHR — is thrown to the
    // layout route's (missing) error boundary and bubbles to RootErrorBoundary,
    // blanking the whole storefront with the offline screen. The guarded fetch
    // degrades to empty/last-good instead. Gated to the cart route (COM-63): a
    // `null` href is the hook's documented "not wanted yet" gate — NOT `""`,
    // which skips the fetch but still builds a non-null requestKey, so
    // `isLoading` would read true forever off /cart and `hydrated` never flip.
    // Guests pass their localStorage product ids via `?id=` so the server can
    // expand them into cards; `href` re-fetches when auth flips or the guest
    // id list changes.
    const href = useMemo(() => {
      if (!isCart) return null;
      const ids = isLoggedIn ? [] : guest.productIds;
      const params = new URLSearchParams();
      for (const id of ids) params.append("id", id);
      const qs = params.toString();
      return qs ? `${url(RESOURCE_URL)}?${qs}` : url(RESOURCE_URL);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCart, isLoggedIn, guest.productIds]);

    // `scope` beacons a contained load failure (once per mount) so a real
    // outage is visible in Workers Logs even though nothing crashes. The name
    // is asserted by the E2E harness ("the contained load failure beacons…"),
    // so it stays `sfl.clientLoader` even though this is the raw hydrate.
    const {
      data: loaded,
      failed,
      isLoading,
    } = useFailSoftResourceState<Awaited<ReturnType<typeof loader>>>(
      href,
      reloadKey,
      "sfl.clientLoader",
    );

    // Auth is AuthProvider's flag — NOT re-derived from this provider's fetch
    // (which now only runs on /cart). Guests still see their localStorage
    // list via `guest.productIds`.
    const serverProductIds = isLoggedIn
      ? (loaded?.productIds ?? [])
      : guest.productIds;
    const serverProducts = loaded?.products ?? [];
    // Settled = loaded OR failed. The hook returns `undefined` data for both
    // "loading" and "failed", so `failed` tells them apart — a failed load is
    // hydrated, just degraded. Sticky across a later refetch (last value is
    // retained) — consumers use `loading` for the in-flight window.
    const hydrated = loaded !== undefined || failed;
    // COM-63 — a FAILED load must read as "couldn't load", NOT "no items
    // saved for later". A guest's list is localStorage, and a BLOCKED read
    // degrades to empty — the same false confident-empty, so count it degraded
    // too (auth is server-sourced and unreachable under blocked cookies, hence
    // guest-only).
    const degraded = failed || (!isLoggedIn && isStorageBlocked());

    useEffect(() => {
      if (optimisticRemoved.size === 0) return;
      const stillRemoved = new Set<string>();
      for (const id of optimisticRemoved) {
        if (serverProductIds.includes(id)) stillRemoved.add(id);
      }
      if (stillRemoved.size !== optimisticRemoved.size) {
        setOptimisticRemoved(stillRemoved);
      }
    }, [serverProductIds, optimisticRemoved]);

    const productIds = useMemo(
      () =>
        optimisticRemoved.size === 0
          ? serverProductIds
          : serverProductIds.filter(id => !optimisticRemoved.has(id)),
      [serverProductIds, optimisticRemoved],
    );
    const products = useMemo(
      () =>
        optimisticRemoved.size === 0
          ? serverProducts
          : serverProducts.filter(p => !optimisticRemoved.has(p.id ?? "")),
      [serverProducts, optimisticRemoved],
    );

    const optimisticRemove = useCallback((productId: string) => {
      setOptimisticRemoved(prev => {
        if (prev.has(productId)) return prev;
        const next = new Set(prev);
        next.add(productId);
        return next;
      });
    }, []);

    // Guest: the localStorage list (correct on every route — it is what the
    // guest mutators read/write). Logged-in: the server list, which only
    // loads on /cart — so this is CART-ONLY for logged-in shoppers and reads
    // `false` everywhere else. No logged-in caller off-cart exists; the guest
    // path is what BasketProvider's save-for-later uses (WARN-4).
    const isInList = useCallback(
      (productId: string): boolean =>
        isLoggedIn ? productIds.includes(productId) : guest.isInList(productId),
      [isLoggedIn, productIds, guest.isInList],
    );

    // Memoised (COM-63 review, NIT-5): an inline object here re-rendered every
    // SFL consumer on every navigation via `useMatches`.
    const value = useMemo(
      () => ({
        productIds,
        products,
        hydrated,
        loading: isLoading,
        degraded,
        isInList,
        guestAdd: isLoggedIn ? noopTrue : guest.add,
        guestRemove: isLoggedIn ? noopTrue : guest.remove,
        optimisticRemove: isLoggedIn ? optimisticRemove : noop,
      }),
      [
        productIds,
        products,
        hydrated,
        isLoading,
        degraded,
        isInList,
        isLoggedIn,
        guest.add,
        guest.remove,
        optimisticRemove,
      ],
    );

    return (
      <SaveForLaterContext.Provider value={value}>
        {children}
      </SaveForLaterContext.Provider>
    );
  },
);
SaveForLaterProvider.displayName = "SaveForLaterProvider";
